import { createClient } from '@supabase/supabase-js';

// Lazy Supabase client creation - waits for env to be loaded
let _supabase: ReturnType<typeof createClient> | null = null;

function getSupabaseClient() {
  if (!_supabase) {
    const env = (window as any).__ENV__;
    if (!env?.SUPABASE_URL || !env?.SUPABASE_ANON_KEY) {
      throw new Error('Missing Supabase config (build-time VITE_* vars — see src/config.ts).');
    }
    _supabase = createClient(env.SUPABASE_URL, env.SUPABASE_ANON_KEY, {
      realtime: { params: { eventsPerSecond: 5 } }
    });
  }
  return _supabase;
}

export interface CreateAccountRequest {
  seeker_id: string;
  password: string;
}

export interface CreateAccountResponse {
  success: boolean;
  user: {
    id: string;
    email: string;
    seeker_id: string;
  };
  session_info?: {
    access_token: string;
    refresh_token: string;
  };
}

export class AccountService {
  /**
   * Create a seeker account for booking (with their chosen password)
   * This creates the auth.users record needed for the foreign key constraint
   */
  static async createAccountForBooking(seekerId: string, password: string): Promise<{ success: boolean; user_id: string }> {
    const supabase = getSupabaseClient();
    
    try {
      const { data, error } = await supabase.functions.invoke('create-seeker-account', {
        body: {
          seeker_id: seekerId,
          password: password,
          for_booking: true // Flag to indicate this is for booking
        }
      });

      if (error) {
        throw new Error(error.message);
      }

      return {
        success: true,
        user_id: data.user.id
      };
    } catch (error) {
      console.error('Failed to create account for booking:', error);
      throw error;
    }
  }

  /**
   * Create a seeker account using their onboarding data
   */
  static async createSeekerAccount(seekerId: string, password: string): Promise<CreateAccountResponse> {
    const supabase = getSupabaseClient();
    
    try {
      const { data, error } = await supabase.functions.invoke('create-seeker-account', {
        body: {
          seeker_id: seekerId,
          password: password
        }
      });

      if (error) {
        throw new Error(`Account creation failed: ${error.message}`);
      }

      if (!data.success) {
        throw new Error(data.error || 'Account creation failed');
      }

      return data as CreateAccountResponse;
    } catch (error) {
      console.error('Account creation error:', error);
      throw error;
    }
  }

  /**
   * Create account with social auth (Google/Apple)
   */
  static async createSeekerAccountWithSocial(
    provider: 'google' | 'apple',
    seekerId: string
  ): Promise<void> {
    const supabase = getSupabaseClient();
    
    try {
      // Store seeker ID for post-auth linking
      localStorage.setItem('pending_seeker_id', seekerId);
      
      const { error } = await supabase.auth.signInWithOAuth({
        provider: provider,
        options: {
          redirectTo: `${window.location.origin}/auth/callback?link_seeker=${seekerId}`,
          queryParams: {
            seeker_id: seekerId
          }
        }
      });

      if (error) {
        throw new Error(`${provider} authentication failed: ${error.message}`);
      }

      // The redirect will handle the rest
    } catch (error) {
      console.error(`${provider} auth error:`, error);
      throw error;
    }
  }

  /**
   * Get seeker ID from localStorage (for account creation)
   */
  static getSeekerIdFromStorage(): string | null {
    try {
      // Try multiple possible keys
      const keys = [
        'ghost_seeker_id',
        'seeker_id', 
        'seekerId',
        'current_seeker_id'
      ];

      for (const key of keys) {
        const value = localStorage.getItem(key);
        if (value && value.trim()) {
          return value.trim();
        }
      }

      return null;
    } catch (error) {
      console.error('Error getting seeker ID from storage:', error);
      return null;
    }
  }
}